// Payment chases from the Owed screen — the pure half. Same shape as the
// Rebook nudge: one tap opens WhatsApp with the message typed, the tap is
// counted, the next chase is held back a week. Wording is the Dropby chase
// playbook (Sites/Dropby/operations/chase-workflow.md), signed Liam, because
// the money side is his; the bank lines match the invoice the customer
// already has.

import { firstName } from './late-message'
import { businessSettings, fillTemplate, wordingTemplate, wordMessage, type BusinessSettings } from './business-settings'
import { roundDay, currencySymbol, monthFirst } from './round-config'

export const CHASE_AFTER_DAYS = 7          // hold between chases
export const STALE_AFTER_DAYS = 30         // a first chase this late skips the casual one
export const MAX_CHASES = 3                // after three, the row says "call"

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

// "2026-03-31" → "31 March" — the playbook's soft date.
export function softDate(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ''))
  if (!m) return ''
  // A US or Canadian round: "March 31" (Step 2 round 4).
  if (monthFirst()) return `${MONTHS[Number(m[2]) - 1] ?? ''} ${Number(m[3])}`.trim()
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] ?? ''}`.trim()
}

export function monthOf(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})/.exec(String(iso ?? ''))
  return m ? (MONTHS[Number(m[2]) - 1] ?? '') : ''
}

function pounds(pence: number | null | undefined): string {
  if (pence === null || pence === undefined) return ''
  return (pence / 100).toFixed(pence % 100 === 0 ? 0 : 2)
}

export type ChaseRung = 'lean' | 'full' | 'soft'

export interface ChaseParts {
  name: string | null | undefined
  date: string | null | undefined   // the clean
  amountPence: number | null | undefined
  // Several jobs (step 2): the job the visit was for, as the customer would say it ("gutters"). Only for a customer
  // with more than one job; left out, every message reads exactly as before.
  job?: string | null
  // The round's today (left out: now), for how long ago the clean was.
  today?: string
}

// Reminder 3's "from when" (bug hunt 8 Oct 2026, owed-today #2): Liam's "from a few months back in September" only once
// the clean really is two months old; before that the day itself, "from 22 September" (the ladder sends reminder 3
// about two weeks after the clean). Left out when the clean has no date.
export const FEW_MONTHS_AFTER_DAYS = 60
export function fromWhen(date: string | null | undefined, today: string = roundDay()): string {
  const on = softDate(date)
  if (!on) return ''
  const days = Math.round((Date.parse(`${today}T12:00:00Z`) - Date.parse(`${date}T12:00:00Z`)) / 86_400_000)
  return days >= FEW_MONTHS_AFTER_DAYS ? `from a few months back in ${monthOf(date)}` : `from ${on}`
}

// Rung 1 casual (the clean was recent), rung 2 the full one with the amount
// and bank lines, rung 3 the soft repeat. A stale first chase goes straight
// to the full wording.
//
// settings takes its words from the signed-in business (businessSettings(),
// filled at boot from /api/me) — no Dropby fallback. bank_lines null (a
// business with no bank details on file, or a worker's identity, which
// never carries them) omits the whole bank-transfer paragraph AND its
// reference sentence, cleanly, rather than printing an empty block.
export function chaseMessage(p: ChaseParts, rung: ChaseRung, settings: BusinessSettings = businessSettings()): string {
  const { key, values } = chaseValues(p, rung, settings)
  return wordMessage(key, values, settings)
}

// The filled-in parts of a reminder, by tag: what chaseMessage writes, and what a WhatsApp wording Meta approved is
// sent with (src/wa-template-words.ts), so the two can never say different things.
export function chaseValues(p: ChaseParts, rung: ChaseRung, settings: BusinessSettings = businessSettings()): { key: 'chase_1' | 'chase_2' | 'chase_3'; values: Record<string, string> } {
  const first = firstName(p.name)
  const amount = pounds(p.amountPence)
  const job = p.job?.trim() || null
  const sign = settings.chase_signoff
  if (rung === 'lean') return { key: 'chase_1', values: { 'first name': first, 'for the job': job ? `for the ${job}` : '', 'your name': sign } }
  if (rung === 'soft') {
    const bankBlock = settings.bank_lines
      ? `\n\nBank transfer to:\n\n${settings.bank_lines}\n\nPlease use the first line of your address or your name as the reference so I can match it up.`
      : ''
    // No price on the visit: "the payment outstanding", never "the outstanding" (wording-brand #6), for the standard and
    // any saved wording that keeps "{amount} outstanding".
    const noAmount = wordingTemplate('chase_3', settings).includes('{amount} outstanding') ? 'payment' : ''
    return { key: 'chase_3', values: { 'first name': first, amount: amount ? `${currencySymbol()}${amount}` : noAmount, 'what was done': job ? `the ${job} we did` : 'your window clean',
      'from when': fromWhen(p.date, p.today), 'bank details': bankBlock, 'your name': sign } }
  }
  const on = softDate(p.date)
  const bankBlock = settings.bank_lines
    ? `\n\nWhen you get a chance, you can pay by bank transfer to:\n\n${settings.bank_lines}\n\nIf you could put the first line of your address or your name as the reference, that helps me match it up.`
    : ''
  return { key: 'chase_2', values: { 'first name': first, 'what was done': job ? `the ${job} we did` : 'the last time you had your windows cleaned', 'on date': on ? `on ${on}` : '',
    'amount line': amount ? `The amount outstanding is ${currencySymbol()}${amount}.` : '', 'bank details': bankBlock, 'your name': sign } }
}

// The payment message for a clean done today (Owed's "Done today · payment due", Liam 8 Oct 2026): what was cleaned,
// what it cost and how to pay, in his words; editable in More > Your messages (payment_today). Sending it counts as the
// first chase.
export function paymentTodayMessage(p: ChaseParts, settings: BusinessSettings = businessSettings()): string {
  return wordMessage('payment_today', paymentValues(p, settings), settings)
}

export function paymentValues(p: ChaseParts, settings: BusinessSettings = businessSettings()): Record<string, string> {
  const amount = pounds(p.amountPence)
  const bankBlock = settings.bank_lines
    ? `\n\nOur bank details are:\n\n${settings.bank_lines}\n\nPlease put the first line of your address/name as the reference on the payment`
    : ''
  return { 'first name': firstName(p.name), jobs: p.job?.trim() || 'windows', amount: amount ? `${currencySymbol()}${amount}` : '', 'bank details': bankBlock, 'your name': settings.chase_signoff }
}

// A chase for money brought over from Squeegee (0043): no one visit to name, so the account's total, in Liam's words
// (Q8): "the £12 still outstanding on your account". Same three rungs and the same bank lines. Not owner-editable: its
// wording is fixed here, with the same tags as Your messages, so a WhatsApp wording can be made from it too.
export const ACCOUNT_WORDING: Record<ChaseRung, string> = {
  lean: "Hi {first name},\n\nHope you're okay. Just a quick reminder about the {amount} still outstanding on your account.\n\nKind regards,\n{your name}",
  full: "Hi {first name},\n\nJust to let you know there's {amount} still outstanding on your account.{bank details}\n\nKind regards,\n{your name}",
  soft: "Hi {first name},\n\nFollowing up again on the {amount} still outstanding on your account. I'd really appreciate it if you could settle this when you get a moment.{bank details}\n\nIf there are any issues please let me know. Thanks so much.\n\nKind regards,\n{your name}",
}
export function accountChaseMessage(name: string | null | undefined, pence: number, rung: ChaseRung, settings: BusinessSettings = businessSettings()): string {
  return fillTemplate(ACCOUNT_WORDING[rung], accountValues(name, pence, rung, settings))
}

export function accountValues(name: string | null | undefined, pence: number, rung: ChaseRung, settings: BusinessSettings = businessSettings()): Record<string, string> {
  const bankBlock = settings.bank_lines && rung !== 'lean'
    ? `\n\nYou can pay by bank transfer to:\n\n${settings.bank_lines}\n\nIf you could put the first line of your address or your name as the reference, that helps me match it up.`
    : ''
  return { 'first name': firstName(name), amount: `${currencySymbol()}${pounds(pence)}`, 'bank details': bankBlock, 'your name': settings.chase_signoff }
}

// Which rung the NEXT chase is: the first is casual unless the clean is
// already a month old; the second is the full one; the third is the soft repeat.
export function nextRung(chaseCount: number, daysSinceClean: number): ChaseRung {
  if (chaseCount <= 0) return daysSinceClean >= STALE_AFTER_DAYS ? 'full' : 'lean'
  if (chaseCount === 1) return 'full'
  return 'soft'
}

export type ChaseStage = 'send' | 'waiting' | 'call'

export function chaseCountOf(cl: { chase_count?: number | null; chased?: number | null }): number {
  const n = Math.max(0, Math.round(Number(cl.chase_count) || 0))
  return n || (cl.chased ? 1 : 0)
}

// "I didn't send that chase" — one rung back down the ladder, for the chase
// the WhatsApp button counted on the tap that was never actually sent (Liam
// opened Mike Christie's draft to read it, 2026-09-14, and the ten-second
// Undo bar had expired by the time WhatsApp was closed again).
//
// The date goes to null, not back to the chase before it: only the LAST
// chase's date was ever kept, and a count with no date is already a shape
// this file handles — "chased before the ladder existed" — which reads as
// chased and lets the next chase go today. Which is the point of saying the
// last one never went.
export function chaseUncounted(cl: { chase_count?: number | null; chased?: number | null }): {
  chased: number
  chase_count: number
  chased_on: null
} {
  const back = Math.max(0, chaseCountOf(cl) - 1)
  return { chased: back >= 1 ? 1 : 0, chase_count: back, chased_on: null }
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function nextChaseFrom(cl: { chased_on?: string | null }): string | null {
  return cl.chased_on ? addDays(cl.chased_on, CHASE_AFTER_DAYS) : null
}

// send    — nothing in the way: the next chase can go now
// waiting — chased within the last week
// call    — three chases sent; messages have not worked
export function chaseStage(cl: { chase_count?: number | null; chased?: number | null; chased_on?: string | null }, today: string): ChaseStage {
  const n = chaseCountOf(cl)
  if (n >= MAX_CHASES) return 'call'
  if (n === 0) return 'send'
  const from = nextChaseFrom(cl)
  // Chased before the ladder existed (no date): treat as ready, not waiting.
  if (!from) return 'send'
  return today >= from ? 'send' : 'waiting'
}
